import _ from 'underscore';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import type {
  DataRefId,
  EntityObject,
  OverreactRequestShape,
  OverreactSpec,
  RequestConfig,
  RequestOptions,
} from '../public-types';
import { useEnvironment } from '../environment';
import {
  createDataNode,
  getDataNode,
  getDataRef,
  getRecords,
  getRecordsById,
  getRecordsFromResponseDataArray,
  updateDataRefWithIds,
} from '../store';
import type { DataRef, Store } from '../store';
import { useDeepMemo } from '../utils/primitive-utilities';
import { getLookupCacheFn } from './helper';
import type { LookupCacheFn } from './helper';
import { getCacheIds } from './lookup-cache';
import { getMergedConfig } from './merge-config';
import type { ConfigBuilder } from './merge-config';
import { OverreactRequest } from './overreact-request';
import { useComponent } from './use-component';

interface PaginationVariables {
  pageSize?: number;
  cursorIndex?: number;
  options?: RequestOptions;
}

export interface PaginationConfig<
  TEntity extends EntityObject,
  TVariables extends PaginationVariables,
> extends RequestConfig<TEntity[]> {
  fetchVariables: TVariables;
  strictMode?: boolean;
  mergeNewRecords?: (_records: TEntity[], _newRecords: TEntity[]) => TEntity[];
  lookupCacheByVariables?: LookupCacheFn<TEntity, TVariables>;
}

function getRecordsDataInDataRef<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: OverreactSpec['requestContract'],
  dataRefId: DataRefId,
) {
  return (getRecords(store, requestContract, dataRefId) || [])
    .map(record => record?.getData())
    .filter(Boolean) as TEntity[];
}

function getRecordsDataById<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: OverreactSpec['requestContract'],
  ids: string[],
) {
  return getRecordsById(store, requestContract, ids)
    .map(record => record?.getData())
    .filter(Boolean) as TEntity[];
}

export function usePagination<
  TEntity extends EntityObject,
  TVariables extends PaginationVariables,
>(
  dataRefId: DataRefId,
  spec: OverreactSpec<TVariables, null, TEntity[], TEntity>,
  config: PaginationConfig<TEntity, TVariables>,
) {
  const componentName = useComponent();
  const memoedConfig = useDeepMemo(config) || config;
  const {
    fetchVariables,
    strictMode = false,
    mergeNewRecords,
    lookupCacheByVariables,
  } = memoedConfig;
  const refId = useRef(dataRefId);
  const { requestContract, environmentLookupFn } = spec;
  const cursorIndex = useRef(0);
  const loadingId = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const environment = useEnvironment(environmentLookupFn);
  const [data, setData] = useState<TEntity[]>();
  const [error, setError] = useState<unknown>();

  const resetInternalState = useCallback(() => {
    loadingId.current = null;
    setIsLoading(false);
    cursorIndex.current = 0;
  }, []);
  if (refId.current !== dataRefId) {
    refId.current = dataRefId;
    resetInternalState();
  }

  const setInternalStateOnResponse = useCallback((records: TEntity[]) => {
    cursorIndex.current = records.length;
  }, []);
  const hasMore = useCallback(() => true, []);

  const loadMoreCallback = useCallback((
    _dataRef: DataRef,
    _updatedIds: string[],
    request?: OverreactRequestShape<TVariables, null, TEntity[], TEntity>,
  ) => {
    if (!environment) {
      return;
    }
    const store = environment.store as Store<TEntity>;
    const recordsData = getRecordsDataInDataRef(store, requestContract, dataRefId);
    const dataRef = getDataRef(store, requestContract, dataRefId);
    dataRef?.clearError();
    setInternalStateOnResponse(recordsData);
    setData(recordsData);
    loadingId.current = null;
    setIsLoading(false);
    dataRef?.registerComponent({ componentName });
    if (request?.id === dataRefId) {
      request.mergedConfig?.onComplete?.(recordsData);
    }
  }, [componentName, dataRefId, environment, requestContract, setInternalStateOnResponse]);

  const onErrorCallback = useCallback((
    _dataRef: DataRef,
    currentError: unknown,
    request?: OverreactRequestShape<TVariables, null, TEntity[], TEntity>,
  ) => {
    if (!environment) {
      return;
    }
    const dataRef = getDataRef(environment.store, requestContract, dataRefId);
    const dataRefError = dataRef?.status.error;
    if (dataRefError) {
      loadingId.current = null;
      setIsLoading(false);
    }
    setError(dataRefError);
    if (request?.id === dataRefId && !_.isUndefined(currentError)) {
      request.mergedConfig?.onError?.(currentError);
    }
  }, [dataRefId, environment, requestContract]);

  const onEntitiesCreated = useCallback((_dataRef: DataRef, newIds: string[]) => {
    if (!environment || !mergeNewRecords) {
      return;
    }
    const store = environment.store as Store<TEntity>;
    const records = getRecordsDataInDataRef(store, requestContract, dataRefId);
    const newRecords = getRecordsDataById(store, requestContract, newIds);
    const recordsToShow = mergeNewRecords(records, newRecords);
    const storeRecordsToShow = getRecordsFromResponseDataArray(store, spec, recordsToShow);
    _dataRef.reset(_.map(storeRecordsToShow, record => record.id));
  }, [dataRefId, environment, mergeNewRecords, requestContract, spec]);

  const dataObserver = useMemo(() => ({
    update: loadMoreCallback,
    onError: onErrorCallback,
    onEntitiesCreated,
  }), [loadMoreCallback, onEntitiesCreated, onErrorCallback]);

  useEffect(() => {
    if (!environment) {
      return _.noop;
    }
    const schemaNode = requestContract.getSchemaNode();
    const dataNode = getDataNode(schemaNode) || createDataNode(schemaNode, environment.store);
    const dataRef = dataNode.getDataRef(dataRefId);
    dataRef.subscribe(dataObserver);
    return () => dataRef.unsubscribe(dataObserver);
  }, [dataObserver, dataRefId, environment, requestContract]);

  const loadMore = useCallback((
    requestConfig?: Partial<RequestConfig<TEntity[]>>
      | ConfigBuilder<RequestConfig<TEntity[]>>,
  ) => {
    if (loadingId.current || !environment) {
      return;
    }
    const { pageSize } = fetchVariables;
    const store = environment.store as Store<TEntity>;
    const recordsData = getRecordsDataInDataRef(store, requestContract, dataRefId);
    const mergedConfig = getMergedConfig(requestConfig, memoedConfig);
    if (typeof pageSize === 'number' && recordsData.length >= cursorIndex.current + pageSize) {
      cursorIndex.current = recordsData.length;
      setData(recordsData);
      mergedConfig.onComplete?.(recordsData);
      return;
    }

    const fetchPolicy = fetchVariables.options?.fetchPolicy || requestContract.fetchPolicy;
    const lookupFn = getLookupCacheFn(lookupCacheByVariables, spec, fetchPolicy);
    if (lookupFn) {
      try {
        const overreactIds = getCacheIds({
          store,
          requestContract,
          variables: fetchVariables,
          lookupFn,
        });
        if (!_.isEmpty(overreactIds)) {
          const dataRef = getDataRef(store, requestContract, dataRefId);
          if (dataRef) {
            updateDataRefWithIds(dataRef, overreactIds);
          }
          return;
        }
      } catch (lookupError) {
        // TODO: log error and send request
      }
    }

    const requestVars = {
      ...fetchVariables,
      cursorIndex: strictMode && typeof pageSize === 'number'
        ? cursorIndex.current - (cursorIndex.current % pageSize)
        : cursorIndex.current,
    } as TVariables;
    loadingId.current = _.uniqueId();
    setIsLoading(true);
    environment.pushRequest(new OverreactRequest({
      id: dataRefId,
      requestContract,
      spec,
      variables: requestVars,
      data: null,
      mergedConfig,
      componentName,
    }));
  }, [
    componentName,
    dataRefId,
    environment,
    fetchVariables,
    lookupCacheByVariables,
    memoedConfig,
    requestContract,
    spec,
    strictMode,
  ]);

  return useMemo(() => [{ data, error }, {
    isLoading,
    hasMore,
    loadMore,
  }] as const, [data, error, hasMore, isLoading, loadMore]);
}
